<template>
  <section v-if="project">
    <article class="card">
      <div class="card__padding">
        <h3 class="title__h3 bold">
          {{ t('literal.event_dates') }}
        </h3>

        <form-notification
          class="margin__t--12"
          :keys="error"
        />

        <fieldset class="margin__t--18">
          <label
            for="event_start_date"
            class="input__label input__label--small"
          >
            {{ t('literal.event_start_date') }}
          </label>

          <input-datepicker
            id="event_start_date"
            class="margin__t--6"
            :available-dates="availableStartDates"
            :open-date="new Date()"
            :show-time="showTime"
            v-model="startDate"
          />
        </fieldset>

        <fieldset class="margin__t--18">
          <label
            for="event_end_date"
            class="input__label input__label--small"
          >
            {{ t('literal.event_end_date') }}
          </label>

          <input-datepicker
            id="event_end_date"
            class="margin__t--6"
            :available-dates="availableEndDates"
            :open-date="openEndDate"
            :show-time="showTime"
            :v="v$.endDate"
            v-model="endDate"
          />
        </fieldset>

        <app-save-button
          class="margin__t--24"
          name="save_project_settings_event"
          :is-loading="loader"
          :is-saved="!isUnsaved"
          @click="handleClickSave()"
        />
      </div>
    </article>

    <modal-confirm
      v-if="show_unsaved_modal"
      @close="cancelLeavingPage()"
      @confirm="leavePage()"
    >
      <template #confirm-content>
        <div class="align--center">
          <p>{{ t('literal.leave_page_without_saving') }}</p>
        </div>
      </template>
    </modal-confirm>
  </section>
</template>

<script setup>
import { computed, defineAsyncComponent } from 'vue'
import { useProjectStore } from '@/stores/ProjectStore'
import { useNotificationStore } from '@/stores/NotificationStore'
import { useI18n } from '@/vendors/i18n'
import { useHead } from '@unhead/vue'
import { useUnsaved } from '@/composables/app/useUnsaved'
import useVuelidate from '@vuelidate/core'
import { minDate } from '@/utils/validators'
import { scrollTo } from '@/utils/utils'
import { addDays } from 'date-fns'

import AppSaveButton from '&/molecules/AppSaveButton'
import InputDatepicker from '&/atoms/InputDatepicker'
const FormNotification = defineAsyncComponent(() => import('&/molecules/FormNotification'))
const ModalConfirm = defineAsyncComponent(() => import('&/organisms/ModalConfirm'))

const { projectSlug } = defineProps({ projectSlug: { type: String, required: true } })

const { t } = useI18n()
const { getProject, patchProject, $resetStore } = useProjectStore()
const { postSideNotification } = useNotificationStore()

const { project, clonedProject, loader, error } = getProject({ id: projectSlug })

useHead({ title: computed(() => `${t('literal.event')} / ${t('literal.settings')} / ${project.value?.name}`) })

const { show_unsaved_modal, isUnsaved, cancelLeavingPage, leavePage } = useUnsaved({ condition: computed(() => JSON.stringify(project.value) !== JSON.stringify(clonedProject.value)) })

const showTime = computed(() => project.value?.show_event_time)

const startDate = computed({
  get: () => clonedProject.value?.event_start_date,
  set: date => clonedProject.value.event_start_date = date
})

const endDate = computed({
  get: () => clonedProject.value?.event_end_date,
  set: date => clonedProject.value.event_end_date = date
})

const openEndDate = computed(() => startDate.value ? addDays(startDate.value, 1) : addDays(new Date(), 1))
const availableStartDates = computed(() => ({ end: endDate.value }))
const availableEndDates = computed(() => ({ start: startDate.value }))

const rules = computed(() => ({
  endDate: startDate.value ? { minDate: minDate(startDate.value) } : {}
}))

const v$ = useVuelidate(rules, { endDate })

const handleClickSave = () => {
  v$.value.$touch()

  if (v$.value.$error) return scrollTo('.input--errors')

  patchProject({ id: project.value.slug, params: { event_start_date: startDate.value, event_end_date: endDate.value } })
    .then(() => {
      $resetStore(['projects'])

      postSideNotification({ text: 'literal.modifications_have_been_saved' })
    })
}
</script>
